/* eslint-disable no-console */
import React from 'react';
import PropTypes from 'prop-types';
import { DragDropContext } from 'react-beautiful-dnd';
import { DocumentShape } from 'builder/Knowledge/types';
import mercuryContext from 'ConnectionManager/mercuryContext';
import { addCompetencyItems,  insertKnowledgeMapAssociation } from 'Workspace/Knowledge/utils';
import { generateCompetencyDocumentToBeUpdated } from 'Workspace/Knowledge/utils/dragAndDrop';
import {
  PARENT,
  CHILD,
  regexSplitByDash,
} from 'constants/knowledgeMap';
import { intl } from "containers/LanguageProvider/IntlGlobalProvider";
import KnowledgeMapDeleteConfirm from 'Workspace/Knowledge/components/KnowledgeMapDeleteConfirm';
import { deleteCompetencyDocumentItem } from 'Workspace/Knowledge/services';
import CompetencyTree from './CompetencyTree';
import ExpandableTreeItem from './ExpandableTreeItem';
import Header from './Header';
import ExpandButton from './ExpandButton';
import CompetencyDocumentItemMenu from './CompetencyDocumentItemMenu';
import KnowledgeMapNode from '../KnowledgeMapNode';
import getNodeType from './nodeTypes';
import { calculatePrefixNumber } from '../../helpers/generatePrefix';
import { KnowledgeMapHeader, CompetencyDocumentObjectivesCount, CompetencyDocumentObjectivesWrapper } from './styles';
import KnowledgeMapCollaborators from '../KnowledgeMapCollaborators';
import { duplicateCompetencyDocumentItem } from '../../helpers/KnowledgeMapDuplicateParser';
import { insertCompetencyDocumentItem } from '../../helpers/insertCompetencyDocumentItem';
import messages from "./messages";

class TreeView extends React.Component {
  static getDerivedStateFromProps(nextProps, prevState) {
    const { selectedItemId } = nextProps;
    const { expandedById, competencyTree } = prevState;

    // If props is giving us a new id, apply it within the expanded state
    if (selectedItemId && expandedById[selectedItemId] === undefined) {
      // We want to expand all items 'down' the tree to the new selected item
      const parentIds = competencyTree.getAllParentsByDeepChildId(selectedItemId) || [];
      const changedExpandedIds = parentIds.reduce(
        (acc, id) => ({ ...acc, [id]: true }),
        { [selectedItemId]: true }
      );

      return {
        ...prevState,
        expandedById: {
          ...expandedById,
          ...changedExpandedIds,
        },
      };
    }
    
    competencyTree.updateDocument(nextProps.competencyDocument);

    return {
      competencyTree,
    };
  }

  static contextType = mercuryContext;

  state = {
    competencyTree: new CompetencyTree(this.props.competencyDocument),
    expandedById: { [this.props.competencyDocument.id]: true },
    isParentDragDisabled : false,
    isChildDragDisabled : false,
    deleteModalVisible: false,
  };

  toggleExpandedItem = (id) => {
    const { expandedById } = this.state;
    this.setState({
      expandedById: {
        ...expandedById,
        [id]: !expandedById[id],
      },
    });
  }

  /**
 * Expand all child nodes
 */
  expandAll = () => {
    const { items, id } = this.props.competencyDocument;

    this.setState({
      expandedById: items.reduce((acc, item) => ({ ...acc, [item.id]: true }), { [id] : true }),
    });
  }

  /**
 * Collapse all child nodes
 */
  collapseAll = () => {
    const { id } = this.props.competencyDocument;

    this.setState({
      expandedById: { [id]: true },
    });
  }

  /**
 * Add node event handler
 * 
 * @param {Object} competencyDocumentItem
 */
  addNodeClickHandler = (competencyDocumentItem, event) => {
    event.stopPropagation();
    this.addCompetencyDocumentItem(competencyDocumentItem);
  }

  /**
 * Adding competency document item
 * 
 * @param {Object} competencyDocumentItem
 * @param {Boolean} isDuplicate
 */
  addCompetencyDocumentItem = async (competencyDocumentItem, isDuplicate) => {
    const { competencyTree, expandedById } = this.state;
    const { apolloClient } = this.context;
    const { competencyDocument,  onSelect} = this.props;

    const newCompetencyItems = isDuplicate ? 
      duplicateCompetencyDocumentItem(
        competencyDocumentItem, 
        competencyTree, 
        competencyDocument,
      ) : 
      [ insertCompetencyDocumentItem(
        competencyDocumentItem,
        expandedById,
        competencyTree,
        competencyDocument,
      ) ];
    let [ parentItemId ] = competencyTree.getAllParentsByDeepChildId(competencyDocumentItem.id);
    if (!parentItemId && !isDuplicate) {
      parentItemId = newCompetencyItems[0].currentParentId;
    }
    const knowledgeMapItemResponse = await addCompetencyItems(
      newCompetencyItems, 
      competencyDocument, 
      apolloClient
    );
    
    await insertKnowledgeMapAssociation(
      knowledgeMapItemResponse, 
      competencyDocument.id, 
      apolloClient,
      parentItemId,
    );

    !isDuplicate && Object.keys(knowledgeMapItemResponse.data).forEach((CompetencyItemKey) => {
      const documentItem = knowledgeMapItemResponse.data[CompetencyItemKey].documentItem;
      onSelect(documentItem);
    });
  }

  /**
 * Duplicate node event handler
 * 
 * @param {Object} competencyDocumentItem
 */
  duplicateItemMenuClickHandler = (competencyDocumentItem) => {
    this.addCompetencyDocumentItem(competencyDocumentItem, true);
  }

  onDragStart = (start) => {
    if(start.type == PARENT){
      this.setState({
        isParentDragDisabled : false,
        isChildDragDisabled : true,
      });
    }else if(start.type == CHILD){
      this.setState({
        isParentDragDisabled : true,
        isChildDragDisabled : false,
      });
    }
  }

  onDragEnd = async (drop) => {
    const { competencyTree } = this.state;
    const { apolloClient } = this.context;
    const destinationDroppableId = drop.destination.droppableId;
    let destinationCompetencyDocumentItem = competencyTree.getItemByTitleKey(destinationDroppableId);
    let sourceCompetencyDocumentItem = competencyTree.getItem(drop.source.index);
    let destinationParentId = '';
    if(!destinationCompetencyDocumentItem){
      const [
        destinationTitleKey,
        destinationItemId,
      ] = destinationDroppableId.split(regexSplitByDash);
      destinationParentId = destinationItemId;
      destinationCompetencyDocumentItem = {...sourceCompetencyDocumentItem, titleKey:destinationTitleKey};
    }
    
    const prefixDecimalNumber = calculatePrefixNumber(
      competencyTree, 
      destinationCompetencyDocumentItem,
    );
    const competencyDocumentItemResult = {...sourceCompetencyDocumentItem};
    const destinationItemAlias = destinationDroppableId.split(regexSplitByDash)[0];
    const inputArguments = {
      sourceCompetencyDocumentItem,
      destinationCompetencyDocumentItem,
      prefixDecimalNumber,
      competencyTree,
      competencyDocumentItemResult,
      destinationParentId,
      destinationItemAlias,
    };
    await generateCompetencyDocumentToBeUpdated(inputArguments, apolloClient);

    this.setState({
      isParentDragDisabled : false,
      isChildDragDisabled : false,
    });
  }

  deleteItemClickHandler = (competencyItem) => {
    const { competencyTree } = this.state;
    const hasChildren = competencyTree.hasChildren(competencyItem.id);
    this.setState({
      deleteModalVisible: true,
      competencyItemToDelete: competencyItem,
      itemToDeleteHasChildren: hasChildren,
    });
  }

  setDeleteModalVisible = (isDeleteModalVisible) => {
    this.setState({ deleteModalVisible: isDeleteModalVisible });
  }

  handleActivityDelete = async () => {
    const { id: competencyDocumentId } = this.props.competencyDocument;
    const { apolloClient } = this.context;
    const { competencyItemToDelete, competencyTree, itemToDeleteHasChildren } = this.state;
    const { id: competencyItemToDeleteId } = competencyItemToDelete;
    if(itemToDeleteHasChildren) {
      const childItems = competencyTree.getChildren(competencyItemToDeleteId);
      childItems.forEach( async (childItem) => {
        await deleteCompetencyDocumentItem(
          competencyTree, 
          competencyDocumentId,
          childItem.id, 
          apolloClient
        );
      });
    }
    
    await deleteCompetencyDocumentItem(
      competencyTree, 
      competencyDocumentId,
      competencyItemToDeleteId,
      apolloClient
    );
    this.setState({ 
      deleteModalVisible: false,
      competencyItemToDelete: null,
    });
  };

  render() {
    const { 
      competencyTree, 
      expandedById, 
      isParentDragDisabled, 
      isChildDragDisabled, 
      deleteModalVisible, 
      itemToDeleteHasChildren,
    } = this.state;
    const { onSelect, selectedItemId, competencyDocument } = this.props;
    const countItems = competencyTree.countAllItems();
    const competencyDocumentParentItems = competencyTree.getRootItems();
    const countParentItems = competencyDocumentParentItems.length;
    return (
      <div>
        <KnowledgeMapHeader>
          <KnowledgeMapCollaborators competencyDocument={competencyDocument}/>
        </KnowledgeMapHeader>
        <Header>
          
          <CompetencyDocumentObjectivesWrapper>
            <CompetencyDocumentObjectivesCount>{countParentItems}</CompetencyDocumentObjectivesCount> 
            {countParentItems === 1 ? intl.formatMessage(messages.competencyDocumentLearningObjectiveSingular) : intl.formatMessage(messages.competencyDocumentLearningObjectivesPlural)}
          </CompetencyDocumentObjectivesWrapper>
          
          { countParentItems !== countItems ? 
            <CompetencyDocumentObjectivesWrapper>
              <CompetencyDocumentObjectivesCount>{countItems}</CompetencyDocumentObjectivesCount>
              {countItems === 1 ? intl.formatMessage(messages.competencyDocumentTotalObjectiveSingular) : intl.formatMessage(messages.competencyDocumentTotalObjectivesPlural)}
            </CompetencyDocumentObjectivesWrapper> : ''
          }
        </Header>

        <DragDropContext onDragEnd={this.onDragEnd} onDragStart={this.onDragStart}>
          <ExpandableTreeItem
            item={competencyDocument}
            competencyTree={competencyTree}
            expandedById={expandedById}
            competencyDocumentParentItems={competencyDocumentParentItems}
            isParentDragDisabled={isParentDragDisabled}
            isChildDragDisabled={isChildDragDisabled}
            addNodeClickHandler={this.addNodeClickHandler}
            render={({ item: childItem, hasDummyChild }) => (
              <KnowledgeMapNode
                item={childItem}
                itemType={getNodeType(competencyTree, childItem.id)}
                showWarningIcon={competencyTree.hasParentsWithoutChildren()}
                onClick={() => onSelect(childItem)}
                selected={childItem.id === selectedItemId}
                isExpanded={expandedById[childItem.id]}
                hasDummyChild={hasDummyChild}
                addNodeClickHandler={(event) => this.addNodeClickHandler(childItem, event)}
                actions={
                  <React.Fragment>
                    { (!competencyTree.isCompetencyDocument(childItem.id) && !(competencyTree.getAllParentsByDeepChildId(childItem.id).length)) && (
                      <ExpandButton
                        onClick={() => this.toggleExpandedItem(childItem.id)}
                        expanded={expandedById[childItem.id]}
                      />
                    )}
                    { !competencyTree.isCompetencyDocument(childItem.id) && 
                    <CompetencyDocumentItemMenu
                      competencyDocumentItem={childItem} 
                      duplicateItemMenuClickHandler={this.duplicateItemMenuClickHandler}
                      deleteItemClickHandler={this.deleteItemClickHandler}/> 
                    }
                  </React.Fragment>
                }
              />
            )}
          />
        </DragDropContext>
        <KnowledgeMapDeleteConfirm
          visible={deleteModalVisible}
          onDismiss={() => {
            this.setDeleteModalVisible(false);
          }}
          isDeleteDocumentItemConfirm={true}
          itemToDeleteHasChildren={itemToDeleteHasChildren}
          handleActivityDelete={this.handleActivityDelete}
        />
      </div>
    );
  }
}

TreeView.propTypes = {
  competencyDocument: DocumentShape.isRequired,
  onSelect: PropTypes.func,
  selectedItemId: PropTypes.string,
};

export default TreeView;

